import { useState, useMemo, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GraceFaqAdminTab } from "@/components/GraceFaqAdminTab";
import { ResponseGuardrailsPanel } from "@/components/ResponseGuardrailsPanel";
import { toast } from "sonner";
import {
  Phone, RefreshCw, MessageSquare, Eye, EyeOff, Lock, Mic,
  LogOut, CheckCircle2, XCircle, PhoneOutgoing, PhoneIncoming,
  PhoneCall, Search, Download, ChevronDown, ChevronUp,
  PhoneMissed, Clock, BarChart2, ArrowUpRight, ArrowDownLeft,
  Loader2, RotateCcw, Calendar, Radio, Save, Settings, DollarSign,
  GraduationCap, ThumbsUp, ThumbsDown, Flag, Tag, Wand2, History, ShieldCheck,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend,
} from "recharts";

// ── Constants ─────────────────────────────────────────────────────────────────
const SESSION_KEY = "bc360_voice_center_unlocked";

type DateFilter  = "today" | "7d" | "all";
type AgentFilter = "all" | "grace" | "dawn";
type StatusFilter = "all" | "success" | "failed";
type DirFilter   = "all" | "inbound" | "outbound";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDur(secs: number | null | undefined): string {
  if (secs == null) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtDateTime(iso: string) {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }),
  };
}

function fmtMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(value));
}

function maskPhone(value: string | null | undefined): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "-";
  return `****${digits.slice(-4)}`;
}

function costLabel(cost: any): string {
  return cost?.twilioCostUsd != null ? fmtMoney(cost.twilioCostUsd) : "—";
}

function costSourceLabel(source: string | null | undefined): string {
  if (source === "actual") return "Actual";
  if (source === "pending") return "Pending";
  if (source === "missing_sid") return "Missing SID";
  if (source === "not_configured") return "Not configured";
  return "Unavailable";
}

function isSuccess(outcome: string | null | undefined): boolean {
  return outcome === "true" || outcome === "success" || outcome === "completed";
}

function isFailed(outcome: string | null | undefined): boolean {
  // Only explicit "false" with no substance is a real failure.
  // "unknown" without reclassification shows as "—" not "Failed".
  return outcome === "false";
}

function isInProgress(outcome: string | null | undefined): boolean {
  return outcome === "in-progress";
}

function exportCSV(calls: any[]) {
  const header = [
    "ID","Date","Time","Agent","Direction","Caller","Callee",
    "Duration (s)","Messages","Outcome","Rep Code",
    "Wibiz Voice Mins","Wibiz Voice Chars",
    "LLM Turns","Avg LLM Latency (ms)","Max LLM Latency (ms)","Avg Response Words",
    "Summary","Conversation ID","Twilio Call SID",
  ].join(",");
  const rows = calls.map((c) => {
    const { date, time } = fmtDateTime(c.timestamp);
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    return [
      c.id, date, time, c.agentLabel, c.direction ?? "unknown",
      c.callerNumber ?? "", c.calleeNumber ?? "",
      c.durationSecs ?? "", c.messageCount ?? "",
      isSuccess(c.outcome) ? "Completed" : isFailed(c.outcome) ? "Failed" : (c.outcome ?? "—"),
      c.repCode ?? "",
      c.cost?.elDurationMins ?? "",
      c.cost?.elCharactersUsed ?? "",
      c.llmTurns ?? "", c.avgLlmLatencyMs ?? "", c.maxLlmLatencyMs ?? "", c.avgResponseWords ?? "",
      esc(c.summary ?? ""), c.conversationId ?? "", c.twilioCallSid ?? c.cost?.twilioCallSid ?? "",
    ].join(",");
  });
  const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), { href: url, download: "ai-call-log.csv" });
  a.click();
  URL.revokeObjectURL(url);
}

// ── Login screen ──────────────────────────────────────────────────────────────
function VoiceCenterLogin({ onUnlock }: { onUnlock: () => void }) {
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);

  const verify = trpc.voiceCenterAuth.verify.useMutation({
    onSuccess: () => { sessionStorage.setItem(SESSION_KEY, "true"); onUnlock(); },
    onError:   () => { toast.error("Incorrect password. Please try again."); setPassword(""); },
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 25px 25px,white 2px,transparent 0)", backgroundSize: "50px 50px" }} />
      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#84ab4a] to-[#6e9038] shadow-lg mb-4">
            <Mic className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">BizConnect360</h1>
          <p className="text-slate-400 text-sm mt-1">AI Call Command Center</p>
        </div>
        <div className="rounded-2xl border border-slate-700 bg-slate-800/80 backdrop-blur shadow-2xl p-6">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-[#84ab4a]" /><h2 className="text-white text-base font-bold">QA Team Access</h2>
          </div>
          <p className="text-slate-400 text-xs text-center mb-5">Enter the Voice Center password to continue.</p>
          <form onSubmit={(e) => { e.preventDefault(); if (password.trim()) verify.mutate({ password }); }} className="space-y-4">
            <div className="relative">
              <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Voice Center password" autoFocus
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 pr-10 focus:border-[#84ab4a]" />
              <button type="button" onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button type="submit" disabled={verify.isPending || !password.trim()}
              className="w-full bg-[#84ab4a] hover:bg-[#6e9038] text-white font-semibold">
              {verify.isPending
                ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Verifying…</span>
                : <span className="flex items-center gap-2"><Lock className="w-4 h-4" />Access Call Center</span>}
            </Button>
          </form>
          <p className="text-center text-xs text-slate-500 mt-5 pt-4 border-t border-slate-700">Read-only · AI call records and transcripts</p>
        </div>
        <p className="text-center text-xs text-slate-600 mt-6">© 2025 BizConnect360 · Powered by Wibiz</p>
      </div>
    </div>
  );
}

// ── Outbound Dialer Panel ─────────────────────────────────────────────────────
function OutboundDialer({
  prefillNumber, onCalled,
}: { prefillNumber: string; onCalled: () => void }) {
  const [agent, setAgent]     = useState<"grace" | "dawn">("grace");
  const [toNumber, setTo]     = useState(prefillNumber);
  const [repCode, setRepCode] = useState("");
  const [lastResult, setLast] = useState<any>(null);
  const [lastError, setErr]   = useState<string | null>(null);

  // Sync if parent changes prefill (callback button)
  if (prefillNumber && prefillNumber !== toNumber && prefillNumber !== "") {
    setTo(prefillNumber);
  }

  const call = trpc.grace.outboundTestCall.useMutation({
    onSuccess: (r) => {
      setLast(r);
      setErr(null);
      onCalled();
      toast.success(`${(r as any).agent === "dawn" ? "Dawn" : "Grace"} call initiated.`);
    },
    onError: (e: any) => {
      setErr(e.message || "Failed to start call.");
      toast.error(e.message || "Failed to start call.");
    },
  });

  const canCall = !!toNumber.trim() && !(agent === "dawn" && !repCode.trim()) && !call.isPending;

  return (
    <div className="space-y-3">
      {/* Agent selector */}
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">Agent</label>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-bold w-fit">
          {(["grace", "dawn"] as const).map(a => (
            <button key={a} onClick={() => setAgent(a)}
              className={`px-4 py-2 capitalize transition-colors ${agent === a ? "bg-[#84ab4a] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
              {a === "grace" ? "Grace  +1 (979) 315-9559" : "Dawn  +1 (979) 978-7337"}
            </button>
          ))}
        </div>
      </div>

      {/* Number + rep code + call */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">Phone Number (E.164)</label>
          <Input value={toNumber} onChange={e => setTo(e.target.value)} placeholder="+15551234567"
            className="font-mono" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
            Rep Code {agent === "dawn" ? <span className="text-rose-400 normal-case font-normal">(required)</span>
              : <span className="normal-case font-normal text-gray-400">(optional)</span>}
          </label>
          <Input value={repCode} onChange={e => setRepCode(e.target.value.toUpperCase())} placeholder="BC-362" />
        </div>
        <Button onClick={() => call.mutate({ toNumber: toNumber.trim(), agent, ...(repCode.trim() ? { repCode: repCode.trim() } : {}) })}
          disabled={!canCall}
          className="bg-[#84ab4a] hover:bg-[#6e9038] text-white font-semibold gap-2">
          {call.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
          {call.isPending ? "Calling…" : "Call Now"}
        </Button>
      </div>

      {/* Result */}
      {lastResult && !lastError && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
            <CheckCircle2 className="w-4 h-4" />
            Call accepted — {(lastResult as any).agent === "dawn" ? "Dawn" : "Grace"} ·{" "}
            <span className="font-normal text-emerald-600">{(lastResult as any).mode} mode</span>
          </div>
          <p className="text-xs text-emerald-600 mt-1">The call is being placed. It should appear in the log below within ~30 seconds of completion.</p>
        </div>
      )}
      {lastError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <div className="text-sm font-semibold text-rose-700">Call failed</div>
          <p className="text-xs text-rose-600 mt-1">{lastError}</p>
          <p className="text-[11px] text-rose-400 mt-1">Make sure the number is E.164 format (+1XXXXXXXXXX) and the agent is configured.</p>
        </div>
      )}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function Stat({ label, value, sub, icon: Icon, color }: {
  label: string; value: string | number; sub?: string;
  icon?: React.ElementType; color?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 shadow-sm flex items-start gap-3">
      {Icon && (
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${color ?? "bg-gray-100"}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 truncate">{label}</div>
        <div className="text-xl font-bold text-gray-900 leading-tight">{value}</div>
        {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

// ── Direction badge ───────────────────────────────────────────────────────────
function DirBadge({ direction }: { direction?: string }) {
  if (direction === "outbound")
    return <Badge variant="outline" className="text-[10px] font-bold border-violet-200 text-violet-700 bg-violet-50 gap-1 pr-2"><ArrowUpRight className="w-2.5 h-2.5" />Out</Badge>;
  if (direction === "inbound")
    return <Badge variant="outline" className="text-[10px] font-bold border-sky-200 text-sky-700 bg-sky-50 gap-1 pr-2"><ArrowDownLeft className="w-2.5 h-2.5" />In</Badge>;
  return <Badge variant="outline" className="text-[10px] text-gray-400">—</Badge>;
}

// ── Training / QA ──────────────────────────────────────────────────────────────
// Feedback loop: call transcript -> issue classification -> corrected answer ->
// knowledge/prompt update -> retest. Flags arrive here two ways: auto-flagged
// by the server (bad-phrase scan on every voice response) or added manually by
// an admin reviewing a transcript below in the call log.

type ReasonTag = "narration_leak" | "wrong_answer" | "hallucination" | "bad_routing" | "unresolved" | "other";
const REASON_TAG_LABELS: Record<ReasonTag, string> = {
  narration_leak: "Narration leak",
  wrong_answer: "Wrong answer",
  hallucination: "Hallucination",
  bad_routing: "Bad routing",
  unresolved: "Unresolved / dead end",
  other: "Other",
};
const PRODUCT_TOPIC_OPTIONS = ["General", "Fundable360", "Scale360", "Share360", "Legacy360", "Media360"];
// Friendly labels for the raw targetSource enum — never render the enum value
// itself, since "ghl_kb_external" would surface "ghl" straight to an admin.
const TARGET_SOURCE_LABELS: Record<string, string> = {
  grace_faq: "Grace FAQ",
  prompt_change: "Prompt change",
  ghl_kb_external: "External Wibiz KB",
};
const STATUS_BADGE: Record<string, string> = {
  open: "bg-rose-50 text-rose-600 border-rose-200",
  corrected: "bg-amber-50 text-amber-700 border-amber-200",
  verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  dismissed: "bg-gray-100 text-gray-400 border-gray-200",
};

function FlagCorrectionForm({ flag, onApplied }: { flag: any; onApplied: () => void }) {
  const [correctedAnswer, setCorrectedAnswer] = useState("");
  const [targetSource, setTargetSource] = useState<"grace_faq" | "prompt_change" | "ghl_kb_external">("grace_faq");
  const [newFaqQuestion, setNewFaqQuestion] = useState(flag.question ?? "");
  const [newFaqCategory, setNewFaqCategory] = useState("Programs & Eligibility");
  // Default the new FAQ's audience to whichever bot this flag actually
  // belongs to (a Grace flag defaults to "grace only", a Dawn flag to "dawn
  // only") — admin can still widen it to "both" if the fix applies to both.
  const defaultAudience = flag.agentLabel === "Dawn" ? "dawn" : "grace";
  const [newFaqAudience, setNewFaqAudience] = useState<"grace" | "dawn" | "both">(defaultAudience);
  const faqLabel = flag.agentLabel === "Dawn" ? "Dawn FAQ" : "Grace FAQ";

  const correct = trpc.voiceQa.correct.useMutation({
    onSuccess: () => { toast.success("Correction applied"); setCorrectedAnswer(""); onApplied(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="rounded-xl border border-gray-100 bg-[#F8FAFC] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Wand2 className="w-3.5 h-3.5 text-[#84ab4a]" />
        <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Apply Correction</span>
      </div>
      <textarea
        value={correctedAnswer}
        onChange={(e) => setCorrectedAnswer(e.target.value)}
        placeholder="Write the correct answer the bot should have given..."
        rows={3}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 resize-none"
      />
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Apply to</label>
          <select value={targetSource} onChange={(e) => setTargetSource(e.target.value as any)}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
            <option value="grace_faq">{faqLabel} (live immediately)</option>
            <option value="prompt_change">Prompt change (needs code deploy)</option>
            <option value="ghl_kb_external">External Wibiz KB (needs manual edit in Wibiz)</option>
          </select>
        </div>
        {targetSource === "grace_faq" && (
          <>
            <div className="min-w-[200px] flex-1">
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">FAQ Question</label>
              <input value={newFaqQuestion} onChange={(e) => setNewFaqQuestion(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Category</label>
              <input value={newFaqCategory} onChange={(e) => setNewFaqCategory(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Audience</label>
              <select value={newFaqAudience} onChange={(e) => setNewFaqAudience(e.target.value as any)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
                <option value="grace">Grace only</option>
                <option value="dawn">Dawn only</option>
                <option value="both">Both</option>
              </select>
            </div>
          </>
        )}
      </div>
      {targetSource === "ghl_kb_external" && (
        <p className="text-[11px] text-amber-600">This records the intended fix here for tracking, but someone still needs to manually edit the article in the Wibiz Knowledge Base — this system cannot write to Wibiz's KB.</p>
      )}
      {targetSource === "prompt_change" && (
        <p className="text-[11px] text-amber-600">This records the intended fix here for tracking, but it will not take effect until a developer edits the system prompt and deploys.</p>
      )}
      <Button
        size="sm"
        disabled={!correctedAnswer.trim() || correct.isPending}
        onClick={() => correct.mutate({
          flagId: flag.id,
          correctedAnswer: correctedAnswer.trim(),
          targetSource,
          newFaqQuestion: targetSource === "grace_faq" ? newFaqQuestion.trim() : undefined,
          newFaqCategory: targetSource === "grace_faq" ? newFaqCategory.trim() : undefined,
          newFaqAudience: targetSource === "grace_faq" ? newFaqAudience : undefined,
        })}
        className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white"
      >
        {correct.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        <span className="ml-1.5">Apply Correction</span>
      </Button>
    </div>
  );
}

// Turns a flagged bad response into an always-on guardrail — prefilled from
// the flag's own question/actualResponse/productTopic so the reviewer isn't
// retyping what they just reviewed. Saves as a draft by default; publishing
// is a separate explicit step (either here or from the Response Guardrails
// tab) so a rule never goes live without a deliberate confirmation.
function CreateGuardrailFromFlag({ flag }: { flag: any }) {
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`${flag.productTopic ?? "General"}: ${(flag.question ?? "").slice(0, 60)}`);
  const [vertical, setVertical] = useState<string>(flag.productTopic ?? "General");
  const [triggerPhrasesText, setTriggerPhrasesText] = useState(flag.question ?? "");
  const [canonicalAnswer, setCanonicalAnswer] = useState("");
  const [mustNotSayText, setMustNotSayText] = useState(flag.actualResponse ? flag.actualResponse.slice(0, 300) : "");
  const [publishNow, setPublishNow] = useState(false);

  const createGuardrail = trpc.voiceQa.createGuardrailFromFlag.useMutation({
    onSuccess: () => {
      toast.success(publishNow ? "Guardrail published" : "Guardrail saved as draft");
      utils.voiceQa.list.invalidate();
      setOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  if (flag.guardrailId) {
    return (
      <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-xs text-emerald-700 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> A response guardrail (#{flag.guardrailId}) was already created from this flag.
      </div>
    );
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1.5 text-xs">
        <ShieldCheck className="w-3.5 h-3.5" /> Create response guardrail
      </Button>
    );
  }

  return (
    <div className="rounded-xl border border-[#1A6FD4]/30 bg-[#1A6FD4]/5 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-[#1A6FD4]" />
        <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">New Response Guardrail</span>
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Vertical</label>
          <select value={vertical} onChange={(e) => setVertical(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
            {PRODUCT_TOPIC_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Example Questions / Trigger Phrases (one per line)</label>
        <textarea value={triggerPhrasesText} onChange={(e) => setTriggerPhrasesText(e.target.value)} rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none resize-none" />
      </div>
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Canonical Approved Answer</label>
        <textarea value={canonicalAnswer} onChange={(e) => setCanonicalAnswer(e.target.value)} rows={3}
          placeholder="The exact correct answer Grace/Dawn must give going forward..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none resize-none" />
      </div>
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Must NOT Say (prefilled from the bad response — edit as needed)</label>
        <textarea value={mustNotSayText} onChange={(e) => setMustNotSayText(e.target.value)} rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none resize-none" />
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} />
          Publish immediately (otherwise saved as a draft for review)
        </label>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!name.trim() || !canonicalAnswer.trim() || !triggerPhrasesText.trim() || createGuardrail.isPending}
          onClick={() => createGuardrail.mutate({
            flagId: flag.id,
            name: name.trim(),
            vertical: vertical as any,
            intentCategory: "custom",
            triggerPhrases: triggerPhrasesText.split("\n").map((s: string) => s.trim()).filter(Boolean),
            canonicalAnswer: canonicalAnswer.trim(),
            mustNotSayPhrases: mustNotSayText.split("\n").map((s: string) => s.trim()).filter(Boolean),
            status: publishNow ? "published" : "draft",
          })}
          className="bg-[#1A6FD4] hover:bg-[#1558b0] text-white"
        >
          {createGuardrail.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
          <span className="ml-1.5">{publishNow ? "Create & Publish" : "Save as Draft"}</span>
        </Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function TrainingQaPanel() {
  const utils = trpc.useUtils();
  const [statusFilter, setStatusFilter] = useState<"open" | "corrected" | "verified" | "dismissed" | "all">("open");
  const [agentFilter, setAgentFilter] = useState<"all" | "Grace" | "Dawn">("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({
    agentLabel: "Grace" as "Grace" | "Dawn",
    question: "", actualResponse: "",
    reasonTag: "wrong_answer" as ReasonTag,
    productTopic: "General" as "General" | "Fundable360" | "Scale360" | "Share360" | "Legacy360" | "Media360",
    notes: "",
  });

  const flagsQuery = trpc.voiceQa.list.useQuery(
    { status: statusFilter === "all" ? undefined : statusFilter, agentLabel: agentFilter === "all" ? undefined : agentFilter },
    { refetchInterval: 30000 },
  );
  const flags = (flagsQuery.data ?? []) as any[];

  const createFlag = trpc.voiceQa.create.useMutation({
    onSuccess: () => {
      utils.voiceQa.list.invalidate();
      toast.success("Flagged for review");
      setShowManualForm(false);
      setManualForm({ agentLabel: "Grace", question: "", actualResponse: "", reasonTag: "wrong_answer", productTopic: "General", notes: "" });
    },
    onError: (e) => toast.error(e.message),
  });
  const updateStatus = trpc.voiceQa.updateStatus.useMutation({
    onSuccess: () => { utils.voiceQa.list.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const classify = trpc.voiceQa.classify.useMutation({
    onSuccess: () => { utils.voiceQa.list.invalidate(); toast.success("Updated"); },
    onError: (e) => toast.error(e.message),
  });

  const counts = useMemo(() => ({ total: flags.length }), [flags]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#84ab4a]/10 flex items-center justify-center shrink-0">
            <GraduationCap className="w-4 h-4 text-[#84ab4a]" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900 text-sm">Training / QA</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">Flagged calls, corrections, and the feedback loop that keeps Grace and Dawn from repeating a bad answer.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowManualForm(v => !v)} className="gap-1.5 text-xs">
            <Flag className="w-3.5 h-3.5" /> Flag a call
          </Button>
          <button onClick={() => flagsQuery.refetch()} disabled={flagsQuery.isFetching}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${flagsQuery.isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Manual flag form */}
      {showManualForm && (
        <div className="rounded-xl border border-[#84ab4a]/30 bg-[#84ab4a]/5 p-4 space-y-3">
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Agent</label>
              <select value={manualForm.agentLabel} onChange={(e) => setManualForm(f => ({ ...f, agentLabel: e.target.value as any }))}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
                <option value="Grace">Grace</option>
                <option value="Dawn">Dawn</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Reason</label>
              <select value={manualForm.reasonTag} onChange={(e) => setManualForm(f => ({ ...f, reasonTag: e.target.value as any }))}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
                {Object.entries(REASON_TAG_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Product/Topic</label>
              <select value={manualForm.productTopic} onChange={(e) => setManualForm(f => ({ ...f, productTopic: e.target.value as any }))}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
                {PRODUCT_TOPIC_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <textarea value={manualForm.question} onChange={(e) => setManualForm(f => ({ ...f, question: e.target.value }))}
            placeholder="The exact question the caller asked..." rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 resize-none" />
          <textarea value={manualForm.actualResponse} onChange={(e) => setManualForm(f => ({ ...f, actualResponse: e.target.value }))}
            placeholder="What the bot actually said..." rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 resize-none" />
          <textarea value={manualForm.notes} onChange={(e) => setManualForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Notes (optional)..." rows={1}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 resize-none" />
          <div className="flex gap-2">
            <Button size="sm" disabled={!manualForm.question.trim() || createFlag.isPending}
              onClick={() => createFlag.mutate(manualForm)}
              className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white">
              {createFlag.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
              <span className="ml-1.5">Flag</span>
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowManualForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-gray-100">
        <FilterGroup label="Status" value={statusFilter} onChange={(v) => setStatusFilter(v as typeof statusFilter)}
          options={[{ v: "all", l: "All" }, { v: "open", l: "Open" }, { v: "corrected", l: "Corrected" }, { v: "verified", l: "Verified" }, { v: "dismissed", l: "Dismissed" }]} />
        <FilterGroup label="Agent" value={agentFilter} onChange={(v) => setAgentFilter(v as typeof agentFilter)}
          options={[{ v: "all", l: "All" }, { v: "Grace", l: "Grace" }, { v: "Dawn", l: "Dawn" }]} />
        <span className="text-[11px] text-gray-400 ml-auto">{counts.total} item{counts.total !== 1 ? "s" : ""}</span>
      </div>

      {/* Queue */}
      {flagsQuery.isLoading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-300" /></div>
      ) : flags.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">
          <GraduationCap className="w-8 h-8 mx-auto mb-2 text-gray-200" />
          No {statusFilter === "all" ? "" : statusFilter} items — nothing needs review right now.
        </div>
      ) : (
        <div className="space-y-2">
          {flags.map((flag) => {
            const isExpanded = expandedId === flag.id;
            return (
              <div key={flag.id} className="rounded-xl border border-gray-100 overflow-hidden">
                <button onClick={() => setExpandedId(isExpanded ? null : flag.id)}
                  className="w-full px-4 py-3 flex items-center gap-2.5 text-left hover:bg-gray-50/70 transition-colors">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${flag.agentLabel === "Grace" ? "bg-[#84ab4a]/10 text-[#84ab4a] border-[#84ab4a]/30" : "bg-violet-50 text-violet-600 border-violet-200"}`}>
                    {flag.agentLabel}
                  </span>
                  {flag.rating === "up" ? <ThumbsUp className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    : flag.rating === "down" ? <ThumbsDown className="w-3.5 h-3.5 text-rose-500 shrink-0" /> : null}
                  {flag.reasonTag && (
                    <span className="text-[10px] font-semibold text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5 shrink-0">
                      {REASON_TAG_LABELS[flag.reasonTag as ReasonTag] ?? flag.reasonTag}
                    </span>
                  )}
                  <span className="text-xs text-gray-700 truncate flex-1">{flag.question || "(no question captured)"}</span>
                  {flag.source === "auto" && <span className="text-[9px] font-bold text-amber-500 shrink-0">AUTO</span>}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${STATUS_BADGE[flag.status] ?? ""}`}>{flag.status}</span>
                  {/* Independent of `status` above — a flag can be "corrected" AND separately reviewer-verified at once. */}
                  {flag.verifiedAt && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600 shrink-0 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  )}
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronUp className="w-4 h-4 text-gray-400 shrink-0 rotate-90" />}
                </button>
                {isExpanded && (
                  <div className="px-4 py-4 border-t border-gray-100 space-y-3 bg-white">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Question</div>
                        <p className="text-gray-700 whitespace-pre-wrap">{flag.question || "—"}</p>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Actual Response</div>
                        <p className="text-gray-700 whitespace-pre-wrap">{flag.actualResponse || "—"}</p>
                      </div>
                    </div>
                    {flag.notes && (
                      <div className="text-xs text-gray-500 italic">{flag.notes}</div>
                    )}
                    {flag.conversationId && (
                      <div className="text-[10px] text-gray-400 font-mono">conversationId: {flag.conversationId}</div>
                    )}

                    {/* Classification */}
                    <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-50">
                      <Tag className="w-3.5 h-3.5 text-gray-400" />
                      <select value={flag.reasonTag ?? ""} onChange={(e) => classify.mutate({ id: flag.id, reasonTag: e.target.value as any })}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
                        <option value="">No reason set</option>
                        {Object.entries(REASON_TAG_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                      </select>
                      <select value={flag.productTopic ?? "General"} onChange={(e) => classify.mutate({ id: flag.id, productTopic: e.target.value as any })}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
                        {PRODUCT_TOPIC_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <div className="ml-auto flex gap-1.5">
                        {!flag.verifiedAt && (
                          <Button size="sm" variant="outline" className="text-xs h-7"
                            onClick={() => updateStatus.mutate({ id: flag.id, status: "verified" })}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Verified
                          </Button>
                        )}
                        {flag.status !== "dismissed" && (
                          <Button size="sm" variant="outline" className="text-xs h-7 text-gray-400"
                            onClick={() => updateStatus.mutate({ id: flag.id, status: "dismissed" })}>
                            <XCircle className="w-3 h-3 mr-1" /> Dismiss
                          </Button>
                        )}
                      </div>
                    </div>

                    <FlagCorrectionForm flag={flag} onApplied={() => utils.voiceQa.list.invalidate()} />

                    <CreateGuardrailFromFlag flag={flag} />

                    <FlagHistory flagId={flag.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Agent Settings — live Wibiz Voice sync ─────────────────────────────────────
const TONE_PRESET_OPTIONS: Array<{ key: "warm-conversational" | "professional-natural" | "friendly-energetic" | "calm-reassuring"; label: string; hint: string }> = [
  { key: "warm-conversational", label: "Warm", hint: "Friendly, personable, conversational" },
  { key: "professional-natural", label: "Professional", hint: "Polished, businesslike, still natural" },
  { key: "friendly-energetic", label: "Friendly", hint: "Upbeat, energetic, enthusiastic" },
  { key: "calm-reassuring", label: "Calm", hint: "Slow, steady, reassuring" },
];

function AgentSettingsPanel() {
  const [activeAgent, setActiveAgent] = useState<"grace" | "dawn">("grace");
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-bold text-gray-900">Agent Settings</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Voice, tone, greeting, and transfer destinations for the live Wibiz Voice agent. Changes here update the
            production agent directly — every save is read back and verified.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1 shrink-0">
          <button type="button" onClick={() => setActiveAgent("grace")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeAgent === "grace" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
            Grace
          </button>
          <button type="button" onClick={() => setActiveAgent("dawn")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeAgent === "dawn" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
            Dawn
          </button>
        </div>
      </div>
      <div className="p-5">
        <AgentSettingsForm key={activeAgent} agent={activeAgent} />
      </div>
    </div>
  );
}

function AgentSettingsForm({ agent }: { agent: "grace" | "dawn" }) {
  const settingsQuery = trpc.voiceAgent.getSettings.useQuery({ agent });
  const voicesQuery = trpc.voiceAgent.listVoices.useQuery();

  const loaded = settingsQuery.data;

  // Local editable state — seeded once from the loaded live values, never from guessed defaults.
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [stability, setStability] = useState<number | null>(null);
  const [similarityBoost, setSimilarityBoost] = useState<number | null>(null);
  const [speed, setSpeed] = useState<number | null>(null);
  const [applyTextNormalization, setApplyTextNormalization] = useState<"system_prompt" | "elevenlabs" | null>(null);
  const [greeting, setGreeting] = useState<string | null>(null);
  const [tonePreset, setTonePreset] = useState<"warm-conversational" | "professional-natural" | "friendly-energetic" | "calm-reassuring" | "custom" | null>(null);
  const [customTone, setCustomTone] = useState("");
  const [voiceSearch, setVoiceSearch] = useState("");
  const [transferEdits, setTransferEdits] = useState<Record<string, string>>({});

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (loaded && !initialized) {
      setVoiceId(loaded.voice.voiceId);
      setStability(loaded.voice.stability);
      setSimilarityBoost(loaded.voice.similarityBoost);
      setSpeed(loaded.voice.speed);
      setApplyTextNormalization(loaded.voice.applyTextNormalization ?? "system_prompt");
      setGreeting(loaded.voice.greeting);
      setInitialized(true);
    }
  }, [loaded, initialized]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const previewMutation = trpc.voiceAgent.previewVoice.useMutation({
    onError: (e) => toast.error(`Preview failed: ${e.message}`),
  });

  function playPreview(vId: string) {
    setPreviewingId(vId);
    previewMutation.mutate({ voiceId: vId, agent }, {
      onSuccess: (res) => {
        setPreviewingId(null);
        const audio = new Audio(`data:${res.mimeType};base64,${res.audioBase64}`);
        audioRef.current = audio;
        audio.play().catch(() => toast.error("Could not play preview audio."));
      },
      onError: () => setPreviewingId(null),
    });
  }

  const voiceSettingsMutation = trpc.voiceAgent.updateVoiceSettings.useMutation();
  const greetingMutation = trpc.voiceAgent.updateGreeting.useMutation();
  const toneMutation = trpc.voiceAgent.updateTonePreset.useMutation();
  const transferMutation = trpc.voiceAgent.updateTransferDestination.useMutation({
    onSuccess: (res, vars) => {
      if (res.verified) toast.success(`${vars.destinationType} transfer number saved and verified.`);
      else toast.error(`Saved but could not verify the ${vars.destinationType} number — please re-check in a moment.`);
      settingsQuery.refetch();
      setTransferEdits((prev) => { const next = { ...prev }; delete next[vars.destinationType]; return next; });
    },
    onError: (e) => toast.error(`Transfer number update failed: ${e.message}`),
  });

  const voiceDirty = initialized && loaded && (
    voiceId !== loaded.voice.voiceId ||
    stability !== loaded.voice.stability ||
    similarityBoost !== loaded.voice.similarityBoost ||
    speed !== loaded.voice.speed ||
    applyTextNormalization !== (loaded.voice.applyTextNormalization ?? "system_prompt")
  );
  const greetingDirty = initialized && loaded && greeting !== loaded.voice.greeting;
  const toneDirty = tonePreset !== null && (tonePreset !== "custom" || customTone.trim().length > 0);
  const transferDirty = Object.keys(transferEdits).length > 0;

  // Part of the "saved voice reverts to Grace" root-cause fix: selecting a
  // voice/greeting/tone/transfer number only ever updates LOCAL state here —
  // nothing reaches the live agent until its own "Save" button is explicitly
  // clicked (voiceSettingsMutation/greetingMutation/toneMutation/
  // transferMutation). Without this guard, an admin who changes a value and
  // then navigates away (or the tab is closed) believing it's saved would
  // see the PREVIOUS value on next load — indistinguishable from the
  // reported "voice reverted" symptom, even though nothing server-side
  // actually reverted anything.
  useEffect(() => {
    const hasUnsaved = voiceDirty || greetingDirty || toneDirty || transferDirty;
    if (!hasUnsaved) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [voiceDirty, greetingDirty, toneDirty, transferDirty]);
  const savingVoice = voiceSettingsMutation.isPending;
  const savingGreeting = greetingMutation.isPending;
  const savingTone = toneMutation.isPending;

  async function handleSaveVoice() {
    if (voiceId === null || stability === null || similarityBoost === null || speed === null) return;
    try {
      const res = await voiceSettingsMutation.mutateAsync({
        agent, voiceId, stability, similarityBoost, speed,
        applyTextNormalization: applyTextNormalization ?? "system_prompt",
      });
      if (res.verified) toast.success("Voice settings saved and verified against the live agent.");
      else toast.error(`Saved but read-back did not match: ${res.mismatches.join(", ")}`);
      settingsQuery.refetch();
    } catch (e: any) {
      toast.error(`Failed to save voice settings: ${e?.message ?? "unknown error"}`);
    }
  }

  async function handleSaveGreeting() {
    if (greeting === null || greeting.trim().length === 0) return;
    try {
      const res = await greetingMutation.mutateAsync({ agent, greeting });
      if (res.verified) toast.success("Greeting saved and verified.");
      else toast.error(`Saved but read-back did not match: ${res.mismatches.join(", ")}`);
      settingsQuery.refetch();
    } catch (e: any) {
      toast.error(`Failed to save greeting: ${e?.message ?? "unknown error"}`);
    }
  }

  async function handleSaveTone() {
    if (tonePreset === null) return;
    if (tonePreset === "custom" && customTone.trim().length === 0) return;
    try {
      const res = await toneMutation.mutateAsync({
        agent,
        presetKey: tonePreset,
        customInstruction: tonePreset === "custom" ? customTone.trim() : undefined,
      });
      if (res.verified) toast.success("Tone updated on the live agent and verified.");
      else toast.error(`Saved but read-back did not match: ${res.mismatches.join(", ")}`);
    } catch (e: any) {
      toast.error(`Failed to update tone: ${e?.message ?? "unknown error"}`);
    }
  }

  function handleReset() {
    if (!loaded) return;
    setVoiceId(loaded.voice.voiceId);
    setStability(loaded.voice.stability);
    setSimilarityBoost(loaded.voice.similarityBoost);
    setSpeed(loaded.voice.speed);
    setApplyTextNormalization(loaded.voice.applyTextNormalization ?? "system_prompt");
    setGreeting(loaded.voice.greeting);
    setTonePreset(null);
    setCustomTone("");
    toast.message("Reverted to last-loaded live values.");
  }

  const voices = voicesQuery.data ?? [];
  const filteredVoices = voiceSearch.trim()
    ? voices.filter((v) => `${v.name} ${v.gender ?? ""} ${v.accent ?? ""} ${v.descriptive ?? ""} ${v.useCase ?? ""}`.toLowerCase().includes(voiceSearch.trim().toLowerCase()))
    : voices;
  const currentVoiceName = voices.find((v) => v.voiceId === voiceId)?.name ?? voiceId ?? "—";

  if (settingsQuery.isLoading) {
    return <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading live {agent === "grace" ? "Grace" : "Dawn"} settings…</div>;
  }
  if (settingsQuery.isError || !loaded) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
        Could not load live settings for {agent === "grace" ? "Grace" : "Dawn"}: {(settingsQuery.error as any)?.message ?? "unknown error"}.
        {" "}This usually means Wibiz Voice is not configured for this agent yet (missing API key or agent ID) — that is external setup, not a code bug.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Voice */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Voice</h3>
          <span className="text-xs text-gray-400">Current: {currentVoiceName}</span>
        </div>
        <Input placeholder="Search voices by name, gender, accent, use case…" value={voiceSearch}
          onChange={(e) => setVoiceSearch(e.target.value)} className="mb-2 text-xs h-8" />
        {voicesQuery.isLoading ? (
          <div className="text-xs text-gray-400 py-2">Loading voice library…</div>
        ) : voicesQuery.isError ? (
          <div className="text-xs text-rose-600 py-2">Could not load the voice library: {(voicesQuery.error as any)?.message}</div>
        ) : (
          <div className="max-h-56 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50">
            {filteredVoices.length === 0 && <div className="text-xs text-gray-400 px-3 py-3">No voices match "{voiceSearch}".</div>}
            {filteredVoices.map((v) => (
              <div key={v.voiceId} className={`flex items-center justify-between px-3 py-2 text-xs ${v.voiceId === voiceId ? "bg-[#f2f7ea]" : ""}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-800 truncate">{v.name}</div>
                  <div className="text-gray-400 truncate">{[v.gender, v.accent, v.descriptive, v.useCase].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                  <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={previewingId === v.voiceId}
                    onClick={() => playPreview(v.voiceId)}>
                    {previewingId === v.voiceId ? <Loader2 className="w-3 h-3 animate-spin" /> : "Preview"}
                  </Button>
                  <Button size="sm" className={`h-6 px-2 text-[11px] ${v.voiceId === voiceId ? "bg-[#84ab4a] text-white" : ""}`}
                    variant={v.voiceId === voiceId ? "default" : "outline"}
                    onClick={() => setVoiceId(v.voiceId)}>
                    {v.voiceId === voiceId ? "Selected" : "Use"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sliders */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-700">Stability</label>
          <p className="text-[11px] text-gray-400 mb-1">Lower = more expressive, higher = more consistent</p>
          <input type="range" min={0} max={1} step={0.05} value={stability ?? 0.5}
            onChange={(e) => setStability(parseFloat(e.target.value))} className="w-full" />
          <div className="text-[11px] text-gray-500">{(stability ?? 0).toFixed(2)}</div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700">Similarity</label>
          <p className="text-[11px] text-gray-400 mb-1">How closely it matches the original voice sample</p>
          <input type="range" min={0} max={1} step={0.05} value={similarityBoost ?? 0.75}
            onChange={(e) => setSimilarityBoost(parseFloat(e.target.value))} className="w-full" />
          <div className="text-[11px] text-gray-500">{(similarityBoost ?? 0).toFixed(2)}</div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700">Speed</label>
          <p className="text-[11px] text-gray-400 mb-1">0.7 = slower, 1.2 = faster</p>
          <input type="range" min={0.7} max={1.2} step={0.05} value={speed ?? 1}
            onChange={(e) => setSpeed(parseFloat(e.target.value))} className="w-full" />
          <div className="text-[11px] text-gray-500">{(speed ?? 1).toFixed(2)}</div>
        </div>
      </div>

      {/* Text normalization */}
      <div>
        <label className="text-xs font-semibold text-gray-700">Text Normalization</label>
        <p className="text-[11px] text-gray-400 mb-1">
          "System Prompt" makes Wibiz Voice follow the pronunciation rules baked into the agent's own prompt (see
          "360 Pronunciation Fix" below) instead of guessing. Keep this on System Prompt — "Wibiz Voice Auto" is the
          mode that caused the "three hundred sixty" glitch by re-expanding our already-correct "three sixty" text.
        </p>
        <div className="flex gap-2">
          {([{ key: "system_prompt", label: "System Prompt" }, { key: "elevenlabs", label: "Wibiz Voice Auto" }] as const).map(({ key, label }) => (
            <button key={key} type="button" onClick={() => setApplyTextNormalization(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${applyTextNormalization === key ? "border-[#84ab4a] bg-[#f2f7ea] text-[#4c6b28]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
              {label}
            </button>
          ))}
        </div>
        {applyTextNormalization !== "system_prompt" && (
          <p className="text-[11px] text-amber-600 font-medium mt-1">
            Not set to System Prompt — brand names like "Fundable360" may be mispronounced as "three hundred sixty" on calls.
          </p>
        )}
      </div>

      {/* 360 pronunciation fix — managed prompt block */}
      <PronunciationFixControl agent={agent} />

      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!voiceDirty || savingVoice} onClick={handleSaveVoice}
          className="bg-[#84ab4a] hover:bg-[#6e9038] text-white gap-1.5 text-xs">
          {savingVoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save voice settings
        </Button>
        {voiceDirty && <span className="text-[11px] text-amber-600 font-medium">Unsaved changes</span>}
      </div>

      {/* Tone */}
      <div className="pt-4 border-t border-gray-100">
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Tone</h3>
        <div className="flex flex-wrap gap-2 mb-2">
          {TONE_PRESET_OPTIONS.map((p) => (
            <button key={p.key} type="button" onClick={() => setTonePreset(p.key)}
              title={p.hint}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${tonePreset === p.key ? "border-[#84ab4a] bg-[#f2f7ea] text-[#4c6b28]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
              {p.label}
            </button>
          ))}
          <button type="button" onClick={() => setTonePreset("custom")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${tonePreset === "custom" ? "border-[#84ab4a] bg-[#f2f7ea] text-[#4c6b28]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
            Custom
          </button>
        </div>
        {tonePreset === "custom" && (
          <textarea value={customTone} onChange={(e) => setCustomTone(e.target.value)} maxLength={2000}
            placeholder="Describe the tone in your own words (max 2000 characters)…"
            className="w-full text-xs border border-gray-200 rounded-lg p-2 h-20 mb-2" />
        )}
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={!toneDirty || savingTone} onClick={handleSaveTone}
            className="bg-[#84ab4a] hover:bg-[#6e9038] text-white gap-1.5 text-xs">
            {savingTone ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Apply tone
          </Button>
          <span className="text-[11px] text-gray-400">Only the managed tone section of the prompt is replaced — the rest is untouched.</span>
        </div>
      </div>

      {/* Greeting */}
      <div className="pt-4 border-t border-gray-100">
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Greeting / first message</h3>
        <textarea value={greeting ?? ""} onChange={(e) => setGreeting(e.target.value)} maxLength={1000}
          className="w-full text-xs border border-gray-200 rounded-lg p-2 h-20 mb-2" />
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={!greetingDirty || savingGreeting} onClick={handleSaveGreeting}
            className="bg-[#84ab4a] hover:bg-[#6e9038] text-white gap-1.5 text-xs">
            {savingGreeting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save greeting
          </Button>
          {greetingDirty && <span className="text-[11px] text-amber-600 font-medium">Unsaved changes</span>}
        </div>
      </div>

      {/* Transfer destinations */}
      <div className="pt-4 border-t border-gray-100">
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Transfer destinations (live agent tool)</h3>
        {!loaded.transfer.found ? (
          <div className="text-xs text-gray-400">No transfer_to_number tool found on this agent's live configuration.</div>
        ) : (
          <div className="space-y-2">
            {loaded.transfer.destinations.map((d) => {
              const editValue = transferEdits[d.type] ?? d.phoneNumber;
              const dirty = editValue !== d.phoneNumber;
              return (
                <div key={d.type} className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600 w-20 shrink-0 capitalize">{d.type}</span>
                  <Input value={editValue} placeholder="+1XXXXXXXXXX" className="text-xs h-8 max-w-[180px]"
                    onChange={(e) => setTransferEdits((prev) => ({ ...prev, [d.type]: e.target.value }))} />
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!dirty || transferMutation.isPending}
                    onClick={() => transferMutation.mutate({ agent, destinationType: d.type as any, phoneNumber: editValue.trim() })}>
                    Save
                  </Button>
                  {dirty && <span className="text-[11px] text-amber-600 font-medium">Unsaved</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="pt-2">
        <Button size="sm" variant="ghost" onClick={handleReset} className="text-xs text-gray-400 gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Reset to last-loaded values
        </Button>
      </div>
    </div>
  );
}

// Status + one-click apply for the "three hundred sixty" TTS mispronunciation
// fix — inserts a managed pronunciation-rules block directly into the live
// agent prompt (the mechanism ElevenLabs' text_normalisation_type="system_prompt"
// mode actually reads from). Idempotent: re-clicking just refreshes the same
// block, never duplicates it.
function PronunciationFixControl({ agent }: { agent: "grace" | "dawn" }) {
  const statusQuery = trpc.voiceAgent.getPronunciationStatus.useQuery({ agent });
  const applyMutation = trpc.voiceAgent.applyPronunciationFix.useMutation({
    onSuccess: (res) => {
      if (res.verified) toast.success("360 pronunciation guidance applied to the live agent and verified.");
      else toast.error("Applied but read-back did not verify — please retry.");
      statusQuery.refetch();
    },
    onError: (e) => toast.error(`Failed to apply pronunciation fix: ${e.message}`),
  });

  return (
    <div className="rounded-xl border border-gray-100 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-xs font-semibold text-gray-700">360 Pronunciation Fix</div>
          <p className="text-[11px] text-gray-400 mt-0.5">
            Inserts an explicit rule into {agent === "grace" ? "Grace's" : "Dawn's"} live prompt so Wibiz Voice reads
            "360" as "three sixty" — not "three hundred sixty" — since Text Normalization above is on System Prompt.
          </p>
        </div>
        {statusQuery.isLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 shrink-0" />
        ) : statusQuery.data?.applied ? (
          <span className="text-[11px] font-semibold text-[#4c6b28] bg-[#f2f7ea] px-2 py-1 rounded-lg shrink-0">Applied</span>
        ) : (
          <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-lg shrink-0">Not applied</span>
        )}
      </div>
      <Button size="sm" className="mt-2 bg-[#84ab4a] hover:bg-[#6e9038] text-white gap-1.5 text-xs"
        disabled={applyMutation.isPending} onClick={() => applyMutation.mutate({ agent })}>
        {applyMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        {statusQuery.data?.applied ? "Reapply fix" : "Apply fix"}
      </Button>
    </div>
  );
}

function FlagHistory({ flagId }: { flagId: number }) {
  const detail = trpc.voiceQa.getById.useQuery({ id: flagId });
  const corrections = (detail.data as any)?.corrections ?? [];
  if (corrections.length === 0) return null;
  return (
    <div className="pt-2 border-t border-gray-50">
      <div className="flex items-center gap-1.5 mb-1.5">
        <History className="w-3 h-3 text-gray-400" />
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Correction History</span>
      </div>
      <div className="space-y-1.5">
        {corrections.map((c: any) => (
          <div key={c.id} className="text-[11px] text-gray-500">
            <span className="font-semibold text-gray-700">{c.appliedBy}</span> applied a <span className="font-mono">{TARGET_SOURCE_LABELS[c.targetSource] ?? c.targetSource}</span> correction on {new Date(c.appliedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}: "{c.correctedAnswer.slice(0, 100)}{c.correctedAnswer.length > 100 ? "…" : ""}"
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main call center ──────────────────────────────────────────────────────────
function CallCenter({ onLogout }: { onLogout: () => void }) {
  // Cost-basis procedures are Wibiz-internal. Keep them disabled on the
  // client-facing Voice Center even if this component is reached directly.
  const showWibizCostBasis = false;
  // Queries — in-memory log
  const callLogsQuery = trpc.grace.callLogs.useQuery({ limit: 100 }, { refetchInterval: 15000 });
  const allCalls: any[] = callLogsQuery.data ?? [];
  const twilioBillingQuery = trpc.grace.twilioBillingCallLogs.useQuery(
    { limit: 50 },
    { enabled: showWibizCostBasis },
  );
  const twilioBillingLogs: any[] = [];

  // Queries — persistent EL history
  const [elFilter, setElFilter] = useState<AgentFilter>("all");
  const [expandedElId, setExpandedElId] = useState<string | null>(null);
  const [elAudioUrl, setElAudioUrl] = useState<Record<string, string>>({});
  const [elAudioLoading, setElAudioLoading] = useState<Set<string>>(new Set());
  const elConvsQuery = trpc.grace.elevenLabsConversations.useQuery(
    { agentFilter: elFilter, limit: 100 },
    { refetchInterval: 30000 },
  );
  const elDetailQuery = trpc.grace.elevenLabsConversationDetail.useQuery(
    { conversationId: expandedElId ?? "" },
    { enabled: !!expandedElId },
  );
  const costStatsQuery = trpc.grace.voiceCallCostStats.useQuery(
    { days: 30 },
    { enabled: showWibizCostBasis },
  );

  // Voice setup — config status + transfer destinations
  const [showSetup, setShowSetup] = useState(true);
  const [showTrainingQa, setShowTrainingQa] = useState(false);
  const [showFaqManager, setShowFaqManager] = useState(false);
  const [showGuardrails, setShowGuardrails] = useState(false);
  const [showAgentSettings, setShowAgentSettings] = useState(false);
  const [transferEdits, setTransferEdits] = useState<Record<string, string>>({});
  const statusQuery = trpc.grace.phoneStatus.useQuery(undefined, { refetchInterval: 30000 });
  const allSettings = trpc.settings.getAll.useQuery();
  const updateSetting = trpc.settings.update.useMutation({
    onSuccess: () => { toast.success("Transfer number saved."); allSettings.refetch(); statusQuery.refetch(); },
    onError: () => toast.error("Failed to save transfer number."),
  });
  const getSettingValue = (key: string, fallback?: string | null) => {
    if (transferEdits[key] !== undefined) return transferEdits[key];
    return allSettings.data?.find((s: any) => s.key === key)?.value ?? fallback ?? "";
  };

  const loadElAudio = async (conversationId: string) => {
    if (elAudioUrl[conversationId] || elAudioLoading.has(conversationId)) return;
    setElAudioLoading(prev => new Set(prev).add(conversationId));
    try {
      const res = await fetch(`/api/grace-phone/el-conversation/${encodeURIComponent(conversationId)}/audio`);
      if (res.ok) {
        const blob = await res.blob();
        setElAudioUrl(prev => ({ ...prev, [conversationId]: URL.createObjectURL(blob) }));
      }
    } catch { /* ignore */ } finally {
      setElAudioLoading(prev => { const s = new Set(prev); s.delete(conversationId); return s; });
    }
  };

  // Filter state — command center
  const [search, setSearch]       = useState("");
  const [dateFilter, setDate]     = useState<DateFilter>("all");
  const [agentFilter, setAgent]   = useState<AgentFilter>("all");
  const [statusFilter, setStatus] = useState<StatusFilter>("all");
  const [dirFilter, setDir]       = useState<DirFilter>("all");

  // Dialer state
  const [dialerOpen, setDialerOpen] = useState(false);
  const [prefillNum, setPrefill]     = useState("");
  const dialerRef = useRef<HTMLDivElement>(null);

  // Nav-toggle panel refs — smooth-scrolled into view when their panel opens
  // (mirrors the dialerRef pattern above), since each panel renders inline
  // further down the page rather than in a modal.
  const trainingQaRef = useRef<HTMLDivElement>(null);
  const faqManagerRef = useRef<HTMLDivElement>(null);
  const guardrailsRef = useRef<HTMLDivElement>(null);

  // Transcript expand
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filtered calls
  const filtered = useMemo(() => {
    const now      = Date.now();
    const day1     = 24 * 60 * 60 * 1000;
    const cutoff7d = now - 7 * day1;
    const cutoff1d = now - day1;

    return allCalls.filter(c => {
      const ts = new Date(c.timestamp).getTime();
      if (dateFilter === "today" && ts < cutoff1d) return false;
      if (dateFilter === "7d"    && ts < cutoff7d) return false;
      if (agentFilter !== "all"  && c.agentLabel?.toLowerCase() !== agentFilter) return false;
      if (statusFilter === "success" && !isSuccess(c.outcome)) return false;
      if (statusFilter === "failed"  && (isSuccess(c.outcome) || isInProgress(c.outcome))) return false;
      if (dirFilter !== "all" && (c.direction ?? "unknown") !== dirFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const haystack = [c.callerNumber, c.calleeNumber, c.repCode, c.agentLabel, c.conversationId, c.summary]
          .filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allCalls, search, dateFilter, agentFilter, statusFilter, dirFilter]);

  // Stats
  const stats = useMemo(() => {
    const total     = filtered.length;
    const inbound   = filtered.filter(c => c.direction === "inbound").length;
    const outbound  = filtered.filter(c => c.direction === "outbound").length;
    const succeeded = filtered.filter(c => isSuccess(c.outcome)).length;
    const inProgress = filtered.filter(c => isInProgress(c.outcome)).length;
    const avgDur    = total ? Math.round(filtered.reduce((s: number, c: any) => s + (c.durationSecs ?? 0), 0) / total) : 0;
    const avgMsgs   = total ? Math.round(filtered.reduce((s: number, c: any) => s + (c.messageCount ?? 0), 0) / total) : 0;
    const elMinutes  = Math.round(filtered.reduce((s: number, c: any) => s + (c.cost?.elDurationMins ?? 0), 0) * 10) / 10;
    const completed = total - inProgress;
    const rate      = completed ? Math.round((succeeded / completed) * 100) : 0;
    return { total, inbound, outbound, inProgress, rate, avgDur, avgMsgs, elMinutes };
  }, [filtered]);

  const twilioBillingTotal = 0;
  const twilioPricedCount = 0;

  // Wibiz Voice per-agent totals from persistent EL conversation history
  const elTotals = useMemo(() => {
    const convs = elConvsQuery.data ?? [];
    const grace = convs.filter((c: any) => c.agentLabel === "Grace");
    const dawn  = convs.filter((c: any) => c.agentLabel === "Dawn");
    const sumMins  = (arr: any[]) => Math.round(arr.reduce((s, c) => s + (c.cost?.elDurationMins ?? 0), 0) * 10) / 10;
    const sumChars = (arr: any[]) => arr.reduce((s: number, c: any) => s + (c.cost?.elCharactersUsed ?? 0), 0);
    const hasChars = (arr: any[]) => arr.some((c: any) => (c.cost?.elCharactersUsed ?? 0) > 0);
    return {
      grace: { count: grace.length, mins: sumMins(grace), chars: sumChars(grace) },
      dawn:  { count: dawn.length,  mins: sumMins(dawn),  chars: sumChars(dawn)  },
      total: { count: convs.length, mins: sumMins(convs), chars: sumChars(convs) },
      hasCharData: hasChars(convs),
    };
  }, [elConvsQuery.data]);

  // Callback helper
  const handleCallback = (phone: string) => {
    setPrefill(phone);
    setDialerOpen(true);
    setTimeout(() => dialerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  // Opens a nav-toggle panel (Training/QA, FAQ, Guardrails) and smooth-scrolls
  // it into view, same pattern as handleCallback above — only scrolls on open,
  // never on close, so dismissing a panel doesn't cause a jarring scroll jump.
  const openPanel = (setter: (v: boolean) => void, ref: React.RefObject<HTMLDivElement | null>) => {
    setter(true);
    setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Sticky header ── */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#84ab4a] flex items-center justify-center shrink-0">
              <Mic className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <span className="font-bold text-gray-900 text-sm truncate">AI Call Command Center</span>
              <span className="hidden sm:inline text-gray-400 text-xs ml-2">· BizConnect360</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Data/table utilities */}
            <Button size="sm" variant="outline" onClick={() => exportCSV(filtered)} title="Export CSV"
              className="hidden sm:flex gap-1.5 text-xs">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => callLogsQuery.refetch()} disabled={callLogsQuery.isFetching}>
              <RefreshCw className={`w-3.5 h-3.5 ${callLogsQuery.isFetching ? "animate-spin" : ""}`} />
            </Button>

            <div className="hidden sm:block w-px h-5 bg-gray-200" />

            {/* Section navigation */}
            <Button size="sm" variant="outline"
              onClick={() => showTrainingQa ? setShowTrainingQa(false) : openPanel(setShowTrainingQa, trainingQaRef)}
              className={`gap-1.5 text-xs ${showTrainingQa ? "border-[#84ab4a] text-[#84ab4a]" : ""}`}
              title="Training / QA">
              <GraduationCap className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Training / QA</span>
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => showFaqManager ? setShowFaqManager(false) : openPanel(setShowFaqManager, faqManagerRef)}
              className={`gap-1.5 text-xs ${showFaqManager ? "border-[#84ab4a] text-[#84ab4a]" : ""}`}
              title="Grace & Dawn FAQ">
              <MessageSquare className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Grace & Dawn FAQ</span>
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => showGuardrails ? setShowGuardrails(false) : openPanel(setShowGuardrails, guardrailsRef)}
              className={`gap-1.5 text-xs ${showGuardrails ? "border-[#84ab4a] text-[#84ab4a]" : ""}`}
              title="Response Guardrails">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Response Guardrails</span>
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowSetup(v => !v)}
              className={`gap-1.5 text-xs ${showSetup ? "border-[#84ab4a] text-[#84ab4a]" : ""}`}
              title="Voice Setup">
              <Settings className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Setup</span>
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowAgentSettings(v => !v)}
              className={`gap-1.5 text-xs ${showAgentSettings ? "border-[#84ab4a] text-[#84ab4a]" : ""}`}
              title="Agent Settings">
              <Mic className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Agent Settings</span>
            </Button>

            <div className="hidden sm:block w-px h-5 bg-gray-200" />

            {/* Primary actions */}
            <Button size="sm" onClick={() => { setDialerOpen(v => !v); setPrefill(""); }}
              className="bg-[#84ab4a] hover:bg-[#6e9038] text-white gap-1.5 text-xs font-semibold">
              <PhoneOutgoing className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">New Call</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={onLogout} className="text-gray-400 hover:text-gray-600" title="Sign out">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 space-y-4">

        {/* ── Voice Setup (config status + transfer destinations) ── */}
        {showSetup && (
          <div className="space-y-4">

            {/* Configuration Status */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-sm font-bold text-gray-900">Voice System Status</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Live configuration health check — all components must show Configured before going live.</p>
                </div>
                <button onClick={() => { statusQuery.refetch(); allSettings.refetch(); }}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                  <RefreshCw className={`w-3.5 h-3.5 ${statusQuery.isFetching ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { label: "API Key",               ok: !!statusQuery.data?.apiKeyPartial,                   hint: statusQuery.data?.apiKeyPartial },
                  { label: "Grace Agent ID",         ok: statusQuery.data?.graceAgentConfigured,              hint: statusQuery.data?.graceAgentIdPartial },
                  { label: "Grace Phone Num ID",     ok: statusQuery.data?.outboundConfigured,                hint: statusQuery.data?.gracePhoneNumIdPartial },
                  { label: "Dawn Agent ID",          ok: statusQuery.data?.dawnAgentConfigured,               hint: statusQuery.data?.dawnAgentIdPartial },
                  { label: "Dawn Phone Num ID",      ok: !!statusQuery.data?.dawnPhoneNumIdPartial,           hint: statusQuery.data?.dawnPhoneNumIdPartial },
                  { label: "Personalization Secret", ok: statusQuery.data?.personalizationSecretConfigured,   hint: null },
                  { label: "Twilio Billing",         ok: statusQuery.data?.twilioBillingConfigured,           hint: statusQuery.data?.twilioAccountSidPartial },
                  { label: "Support Transfer",       ok: statusQuery.data?.transferSupportConfigured,         hint: null, valid: statusQuery.data?.transferSupportNumberValid },
                  { label: "Booking Transfer",       ok: statusQuery.data?.transferBookingConfigured,         hint: null, valid: statusQuery.data?.transferBookingNumberValid },
                  { label: "Specialist Transfer",    ok: statusQuery.data?.transferSpecialistConfigured,      hint: null, valid: statusQuery.data?.transferSpecialistNumberValid },
                ].map(item => {
                  // Configured but not valid E.164 (+15551234567) — a silent
                  // transfer-failure mode: reads as "set" but the telephony
                  // layer will reject the number at dial time.
                  const formatWarning = item.ok && item.valid === false;
                  return (
                  <div key={item.label} className="rounded-xl border border-gray-100 bg-[#F8FAFC] px-4 py-3">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{item.label}</div>
                    <div className={`mt-1 text-sm font-bold flex items-center gap-1.5 ${formatWarning ? "text-amber-600" : item.ok ? "text-emerald-600" : "text-rose-500"}`}>
                      {formatWarning
                        ? <XCircle className="w-3.5 h-3.5" />
                        : item.ok
                        ? <CheckCircle2 className="w-3.5 h-3.5" />
                        : <XCircle className="w-3.5 h-3.5" />}
                      {formatWarning ? "Invalid Format" : item.ok ? "Configured" : "Missing"}
                    </div>
                    {formatWarning && (
                      <div className="mt-0.5 text-[11px] text-amber-600">Expected E.164, e.g. +15551234567 — transfers to this number will likely fail.</div>
                    )}
                    {item.hint && (
                      <div className="mt-0.5 text-[11px] font-mono text-gray-400 truncate">{item.hint}</div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Transfer Destinations */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              {statusQuery.data?.transferToolStructurallyMissing && (
                <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
                  <div className="text-xs font-bold text-rose-700 uppercase tracking-wide mb-1">Transfers are not functional right now</div>
                  <div className="text-xs text-rose-700">{statusQuery.data.transferToolWarning}</div>
                </div>
              )}
              {statusQuery.data?.transferToolPendingLiveVerification && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">Transfers rewired — pending live verification</div>
                  <div className="text-xs text-amber-700">{statusQuery.data.transferToolPendingLiveVerificationNote}</div>
                </div>
              )}
              {statusQuery.data?.transferDestinationsShareOneNumber && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                  Support, Booking, and Specialist all currently point to the same number — a failure report for one destination means all three have the same underlying cause.
                </div>
              )}
              <div className="mb-4">
                <h2 className="text-sm font-bold text-gray-900">Transfer Destinations</h2>
                <p className="text-xs text-gray-500 mt-0.5">Update the phone numbers the voice agents use for support, booking, and specialist transfers. Number changes save immediately, but see the warning above if transfers aren't triggering at all.</p>
              </div>
              <div className="space-y-4">
                {[
                  { key: "grace_transfer_support_number",    label: "Support Transfer Number",    fallback: statusQuery.data?.transferSupportNumber },
                  { key: "grace_transfer_booking_number",    label: "Booking Transfer Number",    fallback: statusQuery.data?.transferBookingNumber },
                  { key: "grace_transfer_specialist_number", label: "Specialist Transfer Number", fallback: statusQuery.data?.transferSpecialistNumber },
                ].map(item => (
                  <div key={item.key} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                        {item.label}
                      </label>
                      <Input
                        value={getSettingValue(item.key, item.fallback)}
                        onChange={e => setTransferEdits(prev => ({ ...prev, [item.key]: e.target.value }))}
                        placeholder="+15551234567"
                        className="font-mono"
                      />
                    </div>
                    <Button
                      className="bg-[#84ab4a] hover:bg-[#6e9038] text-white gap-1.5"
                      disabled={updateSetting.isPending}
                      onClick={() => updateSetting.mutate({
                        key: item.key,
                        value: getSettingValue(item.key, item.fallback).trim() || null,
                      })}
                    >
                      <Save className="w-4 h-4" /> Save
                    </Button>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* ── Training / QA ── */}
        {showTrainingQa && <div ref={trainingQaRef}><TrainingQaPanel /></div>}

        {/* ── Dawn/Grace FAQ Manager — same shared FAQ library used by the Admin Dashboard ── */}
        {showFaqManager && (
          <div ref={faqManagerRef} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <GraceFaqAdminTab />
          </div>
        )}

        {/* ── Response Guardrails — self-service vertical-specific answer rules ── */}
        {showGuardrails && (
          <div ref={guardrailsRef} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <ResponseGuardrailsPanel />
          </div>
        )}

        {/* ── Agent Settings — live Wibiz Voice sync (voice, tone, greeting, transfer destinations) ── */}
        {showAgentSettings && <AgentSettingsPanel />}

        {/* ── Outbound dialer ── */}
        {dialerOpen && (
          <div ref={dialerRef} className="bg-white rounded-2xl border border-[#84ab4a]/30 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#84ab4a] flex items-center justify-center">
                  <PhoneOutgoing className="w-3.5 h-3.5 text-white" />
                </div>
                <h2 className="font-bold text-gray-900 text-sm">New Outbound Call</h2>
              </div>
              <button onClick={() => setDialerOpen(false)} className="text-gray-400 hover:text-gray-600">
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
            <OutboundDialer prefillNumber={prefillNum} onCalled={() => setTimeout(() => callLogsQuery.refetch(), 3000)} />
          </div>
        )}

        {/* ── Stats row ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat label="Total Calls"   value={stats.total}                          icon={BarChart2}      color="bg-gray-500" />
          <Stat label="Inbound"       value={stats.inbound}                        icon={PhoneIncoming}  color="bg-sky-500"  />
          <Stat label="Outbound"      value={stats.outbound}                       icon={PhoneOutgoing}  color="bg-violet-500" />
          <Stat label="In Progress"   value={stats.inProgress || "—"}              icon={Radio}          color="bg-orange-400"
            sub={stats.inProgress ? "Waiting for completion" : undefined} />
          <Stat label="Success Rate"  value={stats.total ? `${stats.rate}%` : "—"} icon={CheckCircle2}   color="bg-emerald-500" />
          <Stat label="Wibiz Voice"   value={stats.elMinutes ? `${stats.elMinutes}m` : "—"} icon={Mic} color="bg-[#84ab4a]"
            sub="voice minutes (this view)" />
        </div>

        {/* ── Wibiz Voice usage — Grace & Dawn breakdown ── */}
        {elTotals.total.count > 0 && (
          <div className="bg-white rounded-2xl border border-[#84ab4a]/20 shadow-sm px-5 py-4">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-7 h-7 rounded-lg bg-[#84ab4a]/10 flex items-center justify-center shrink-0">
                <Mic className="w-3.5 h-3.5 text-[#84ab4a]" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 text-sm">Wibiz Voice Usage</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">Credit usage for Grace and Dawn across all logged sessions.</p>
              </div>
              {elConvsQuery.isFetching && <Loader2 className="w-3.5 h-3.5 text-gray-300 animate-spin ml-auto" />}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-[10px] font-bold uppercase tracking-wide text-gray-400 pb-2 pr-6">Agent</th>
                    <th className="text-right text-[10px] font-bold uppercase tracking-wide text-gray-400 pb-2 pr-6">Sessions</th>
                    <th className="text-right text-[10px] font-bold uppercase tracking-wide text-gray-400 pb-2 pr-6">Voice Minutes</th>
                    {elTotals.hasCharData && (
                      <th className="text-right text-[10px] font-bold uppercase tracking-wide text-gray-400 pb-2 pr-6">Characters Used</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {([
                    { label: "Grace", data: elTotals.grace, color: "text-[#84ab4a] font-semibold" },
                    { label: "Dawn",  data: elTotals.dawn,  color: "text-violet-600 font-semibold" },
                  ] as const).map(({ label, data, color }) => (
                    <tr key={label} className="border-b border-gray-50">
                      <td className={`py-2.5 pr-6 ${color}`}>{label}</td>
                      <td className="text-right py-2.5 pr-6 text-gray-600">{data.count > 0 ? data.count : "—"}</td>
                      <td className="text-right py-2.5 pr-6 font-semibold text-gray-900">{data.mins > 0 ? `${data.mins}m` : "—"}</td>
                      {elTotals.hasCharData && (
                        <td className="text-right py-2.5 pr-6 text-gray-700">{data.chars > 0 ? data.chars.toLocaleString() : "—"}</td>
                      )}
                    </tr>
                  ))}
                  <tr className="bg-gray-50/60 font-bold">
                    <td className="py-2.5 pr-6 text-gray-700">Total</td>
                    <td className="text-right py-2.5 pr-6 text-gray-900">{elTotals.total.count > 0 ? elTotals.total.count : "—"}</td>
                    <td className="text-right py-2.5 pr-6 text-gray-900">{elTotals.total.mins > 0 ? `${elTotals.total.mins}m` : "—"}</td>
                    {elTotals.hasCharData && (
                      <td className="text-right py-2.5 pr-6 text-gray-900">{elTotals.total.chars > 0 ? elTotals.total.chars.toLocaleString() : "—"}</td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>

            {!elTotals.hasCharData && (
              <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
                Character usage is returned by the Wibiz Voice API per session when available. Expand a session in the history below to load its full detail.
              </p>
            )}
          </div>
        )}

        {/* ── Daily Cost Trend (30d) ── */}
        {showWibizCostBasis && costStatsQuery.data && costStatsQuery.data.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <DollarSign className="w-3.5 h-3.5 text-amber-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 text-sm">Daily Cost Trend — Last 30 Days</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">Twilio + Wibiz Voice daily spend from persisted call log.</p>
              </div>
              {costStatsQuery.isFetching && <Loader2 className="w-3.5 h-3.5 text-gray-300 animate-spin ml-auto" />}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart
                data={costStatsQuery.data}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: string) => {
                    const d = new Date(v);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                />
                <YAxis
                  tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  tickLine={false}
                  axisLine={false}
                  width={54}
                />
                <RechartsTooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e5e7eb" }}
                  formatter={(v: any, name: string) => [`$${Number(v).toFixed(4)}`, name]}
                  labelFormatter={(label: string) => {
                    const d = new Date(label);
                    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                  }}
                />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Area
                  type="monotone"
                  dataKey="twilioCost"
                  name="Twilio"
                  stroke="#f59e0b"
                  fill="#fef3c7"
                  strokeWidth={2}
                  dot={false}
                />
                <Area
                  type="monotone"
                  dataKey="elCost"
                  name="Wibiz Voice"
                  stroke="#84ab4a"
                  fill="#f0f7e8"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── Filter bar ── */}
        {showWibizCostBasis && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 pt-5 pb-4 border-b border-gray-100">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-gray-900 text-sm">Twilio Billing Log</h2>
                  <p className="text-[11px] text-gray-400 mt-0.5">Direct Twilio call charges. Use this when Wibiz Voice history does not include a Call SID.</p>
                </div>
              </div>
              <button onClick={() => twilioBillingQuery.refetch()} disabled={twilioBillingQuery.isFetching}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                title="Refresh Twilio billing log">
                <RefreshCw className={`w-3.5 h-3.5 ${twilioBillingQuery.isFetching ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {twilioBillingQuery.isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
            </div>
          )}

          {!twilioBillingQuery.isLoading && twilioBillingLogs.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-semibold text-gray-400">
                {statusQuery.data?.twilioBillingConfigured ? "No Twilio calls returned yet." : "Twilio billing is not configured."}
              </p>
            </div>
          )}

          {twilioBillingLogs.length > 0 && (
            <>
              <div className="hidden lg:grid grid-cols-[92px_1fr_110px_88px_96px_96px_112px] gap-2 px-4 py-2.5 text-[10px] font-bold uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50/80">
                <div>Date</div><div>Caller / Callee</div><div>Direction</div><div>Duration</div><div>Charge</div><div>Status</div><div>Call SID</div>
              </div>
              <div className="divide-y divide-gray-50">
                {twilioBillingLogs.slice(0, 12).map((call: any) => {
                  const { date, time } = fmtDateTime(call.timestamp);
                  const direction = String(call.direction ?? "");
                  const dirKind = direction.includes("inbound") ? "inbound" : direction.includes("outbound") ? "outbound" : "unknown";
                  return (
                    <div key={call.id} className="grid grid-cols-[1fr_auto] lg:grid-cols-[92px_1fr_110px_88px_96px_96px_112px] gap-2 items-center px-4 py-3 hover:bg-gray-50/70">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-700">{date}</div>
                        <div className="text-[10px] text-gray-400">{time}</div>
                      </div>
                      <div className="hidden lg:block text-xs font-mono text-gray-500 min-w-0">
                        {maskPhone(call.fromNumber)} <span className="text-gray-300 mx-1">-&gt;</span> {maskPhone(call.toNumber)}
                      </div>
                      <div className="hidden lg:block">
                        <DirBadge direction={dirKind} />
                      </div>
                      <div className="hidden lg:flex items-center gap-1 text-xs font-semibold text-gray-700">
                        <Clock className="w-3 h-3 text-gray-300" />{fmtDur(call.durationSecs)}
                      </div>
                      <div className="text-right lg:text-left">
                        <div className="text-xs font-bold text-amber-700 flex items-center gap-1 justify-end lg:justify-start">
                          {fmtMoney(call.priceUsd)}
                          {call.legCount > 1 && (
                            <span title={`Includes ${call.legCount} Twilio-billed legs (call + AI voice bridge)`}
                              className="text-[9px] font-semibold text-amber-500 bg-amber-50 border border-amber-200 rounded px-1">
                              ×{call.legCount}
                            </span>
                          )}
                        </div>
                        <div className="lg:hidden text-[10px] text-gray-400 font-mono mt-0.5">{maskPhone(call.fromNumber)} -&gt; {maskPhone(call.toNumber)}</div>
                      </div>
                      <div className="hidden lg:block">
                        <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500 bg-white">
                          {call.status ?? "â€”"}
                        </Badge>
                      </div>
                      <div className="hidden lg:block font-mono text-[10px] text-gray-400 truncate" title={call.callSid}>
                        {call.callSidPartial ?? "â€”"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        )}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search phone, rep code, summary…"
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:border-[#84ab4a] focus:bg-white transition" />
          </div>

          {/* Date */}
          <FilterGroup label="Date" value={dateFilter} onChange={v => setDate(v as DateFilter)}
            options={[{ v: "all", l: "All Time" }, { v: "7d", l: "Last 7 Days" }, { v: "today", l: "Today" }]} />

          {/* Direction */}
          <FilterGroup label="Direction" value={dirFilter} onChange={v => setDir(v as DirFilter)}
            options={[{ v: "all", l: "All" }, { v: "inbound", l: "Inbound" }, { v: "outbound", l: "Outbound" }]} />

          {/* Agent */}
          <FilterGroup label="Agent" value={agentFilter} onChange={v => setAgent(v as AgentFilter)}
            options={[{ v: "all", l: "All" }, { v: "grace", l: "Grace" }, { v: "dawn", l: "Dawn" }]} />

          {/* Status */}
          <FilterGroup label="Status" value={statusFilter} onChange={v => setStatus(v as StatusFilter)}
            options={[{ v: "all", l: "All" }, { v: "success", l: "Success" }, { v: "failed", l: "Failed" }]} />

          {/* Reset */}
          {(search || dateFilter !== "all" || agentFilter !== "all" || statusFilter !== "all" || dirFilter !== "all") && (
            <button onClick={() => { setSearch(""); setDate("all"); setAgent("all"); setStatus("all"); setDir("all"); }}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition">
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          )}

          {/* Export mobile */}
          <button className="sm:hidden text-gray-400 hover:text-gray-600" onClick={() => exportCSV(filtered)} title="Export CSV">
            <Download className="w-4 h-4" />
          </button>
        </div>

        {/* ── Call log table ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

          {/* Column header */}
          {filtered.length > 0 && (
            <div className="hidden lg:grid grid-cols-[60px_64px_80px_1fr_130px_70px_72px_96px_96px] gap-2 px-4 py-2.5
              text-[10px] font-bold uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50/80">
              <div>Dir</div><div>Agent</div><div>Date</div><div>Time</div>
              <div>Caller / Callee</div><div>Duration</div><div>Avg LLM</div>
              <div>Status</div><div></div>
            </div>
          )}

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              {callLogsQuery.isLoading
                ? <Loader2 className="w-8 h-8 text-[#84ab4a] animate-spin" />
                : <>
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                    <PhoneMissed className="w-7 h-7 text-gray-300" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-500">No calls match your filters</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {allCalls.length === 0
                        ? "Call records appear here ~30 seconds after a completed AI call."
                        : "Try adjusting the filters above."}
                    </p>
                  </div>
                </>
              }
            </div>
          )}

          {/* Rows */}
          <div className="divide-y divide-gray-50">
            {filtered.map((call: any) => {
              const open      = expandedId === call.id;
              const won       = isSuccess(call.outcome);
              const pending   = isInProgress(call.outcome);
              const dur       = fmtDur(call.durationSecs);
              const { date, time } = fmtDateTime(call.timestamp);
              const caller    = call.callerNumber ? `****${String(call.callerNumber).slice(-4)}` : null;
              const callee    = call.calleeNumber ? `****${String(call.calleeNumber).slice(-4)}` : null;
              const isDawn    = call.agentLabel === "Dawn";
              const hasTranscript = !pending && ((call.transcript && call.transcript.length > 0) || call.summary);

              return (
                <div key={call.id}>
                  {/* ── Row ── */}
                  <div className={`grid grid-cols-[60px_64px_auto_1fr] lg:grid-cols-[60px_64px_80px_1fr_130px_70px_72px_96px_96px]
                    gap-2 items-center px-4 py-3 transition-colors ${pending
                      ? "bg-orange-50/60 hover:bg-orange-50"
                      : "hover:bg-gray-50/70"}`}>

                    {/* Direction */}
                    <div><DirBadge direction={call.direction} /></div>

                    {/* Agent */}
                    <div>
                      <Badge variant="outline"
                        className={`text-[10px] font-bold uppercase ${isDawn
                          ? "border-violet-200 text-violet-700 bg-violet-50"
                          : "border-emerald-200 text-emerald-700 bg-emerald-50"}`}>
                        {call.agentLabel}
                      </Badge>
                    </div>

                    {/* Date — hidden on mobile */}
                    <div className="hidden lg:block text-xs font-medium text-gray-700">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-gray-300" />{date}</span>
                    </div>

                    {/* Time + mobile meta */}
                    <div className="min-w-0">
                      <div className="text-xs text-gray-500">{time}</div>
                      {/* Mobile: show caller+dur inline */}
                      <div className="lg:hidden text-[10px] text-gray-400 font-mono mt-0.5 truncate">
                        {caller || callee || "—"} · {dur}
                      </div>
                    </div>

                    {/* Caller/callee */}
                    <div className="hidden lg:block text-xs font-mono text-gray-500">
                      {caller && <span>{caller}</span>}
                      {caller && callee && caller !== callee && <span className="text-gray-300 mx-1">→</span>}
                      {callee && caller !== callee && <span className="text-gray-400">{callee}</span>}
                      {!caller && !callee && "—"}
                      {call.repCode && <span className="ml-1.5 text-[10px] text-[#84ab4a] font-bold">{call.repCode}</span>}
                    </div>

                    {/* Duration */}
                    <div className="hidden lg:flex items-center gap-1 text-xs font-semibold text-gray-700">
                      <Clock className="w-3 h-3 text-gray-300" />{dur}
                    </div>

                    {/* Avg LLM latency */}
                    <div className="hidden lg:block min-w-0">
                      {call.avgLlmLatencyMs != null ? (
                        <>
                          <div className={`text-xs font-mono font-semibold ${
                            call.avgLlmLatencyMs < 500
                              ? "text-emerald-600"
                              : call.avgLlmLatencyMs < 1500
                              ? "text-amber-500"
                              : "text-rose-600"
                          }`}>
                            {call.avgLlmLatencyMs}ms
                          </div>
                          <div className="text-[9px] text-gray-400">avg LLM</div>
                        </>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </div>

                    {/* Status */}
                    <div className="hidden lg:block">
                      {pending ? (
                        <Badge variant="outline"
                          className="text-[10px] flex items-center gap-1 w-fit border-orange-200 text-orange-600 bg-orange-50 animate-pulse">
                          <Radio className="w-2.5 h-2.5" />In Progress
                        </Badge>
                      ) : won ? (
                        <Badge variant="outline"
                          className="text-[10px] flex items-center gap-1 w-fit border-emerald-200 text-emerald-700 bg-emerald-50">
                          <CheckCircle2 className="w-2.5 h-2.5" />Completed
                        </Badge>
                      ) : isFailed(call.outcome) ? (
                        <Badge variant="outline"
                          className="text-[10px] flex items-center gap-1 w-fit border-rose-200 text-rose-700 bg-rose-50">
                          <XCircle className="w-2.5 h-2.5" />Failed
                        </Badge>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-1.5">
                      {/* Callback button — uses full number from log */}
                      {call.callerNumber && (
                        <button title="Call back this number"
                          onClick={() => handleCallback(call.callerNumber)}
                          className="flex items-center gap-1 text-[10px] font-semibold text-violet-500 hover:text-violet-700 transition-colors">
                          <RotateCcw className="w-3 h-3" /><span className="hidden sm:inline">Call Back</span>
                        </button>
                      )}
                      {/* Transcript toggle */}
                      <button onClick={() => setExpandedId(open ? null : call.id)}
                        className={`flex items-center gap-1 text-[10px] font-semibold transition-colors ${hasTranscript ? "text-[#84ab4a] hover:text-[#6e9038]" : "text-gray-300 cursor-default"}`}
                        disabled={!hasTranscript}>
                        <MessageSquare className="w-3 h-3" />
                        <span className="hidden sm:inline">{open ? "Hide" : "Transcript"}</span>
                        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>

                  {/* ── Transcript drawer ── */}
                  {open && (
                    <div className="border-t border-gray-100 bg-gradient-to-b from-gray-50 to-white px-4 py-4 space-y-4">

                      {/* Meta ribbon */}
                      <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
                        {call.conversationId && (
                          <span className="font-mono bg-gray-100 rounded px-2 py-0.5 text-gray-500">
                            ID: {call.conversationId}
                          </span>
                        )}
                        {call.direction && (
                          <span className="flex items-center gap-1">
                            {call.direction === "outbound" ? <ArrowUpRight className="w-3 h-3 text-violet-400" /> : <ArrowDownLeft className="w-3 h-3 text-sky-400" />}
                            {call.direction === "outbound" ? "Outbound" : "Inbound"} call
                          </span>
                        )}
                        {call.repCode && <span className="font-semibold text-[#84ab4a]">Rep: {call.repCode}</span>}
                        {call.durationSecs != null && (
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-gray-300" />{fmtDur(call.durationSecs)}</span>
                        )}
                        {call.messageCount != null && (
                          <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3 text-gray-300" />{call.messageCount} turns</span>
                        )}
                        {call.cost?.twilioCostUsd != null && (
                          <span className="flex items-center gap-1 font-semibold text-amber-600">
                            <DollarSign className="w-3 h-3" />{fmtMoney(call.cost.twilioCostUsd)} {costSourceLabel(call.cost.twilioBillingSource).toLowerCase()}
                          </span>
                        )}
                        {call.cost?.elDurationMins != null && (
                          <span className="flex items-center gap-1 font-semibold text-[#84ab4a]">
                            <Mic className="w-3 h-3" />{call.cost.elDurationMins}m Wibiz Voice
                          </span>
                        )}
                        {call.outcome != null && (
                          <span className={`flex items-center gap-1 font-semibold ${isSuccess(call.outcome) ? "text-emerald-600" : "text-rose-600"}`}>
                            {isSuccess(call.outcome) ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {isSuccess(call.outcome) ? "Completed successfully" : "Did not complete"}
                          </span>
                        )}
                      </div>

                      {/* AI Summary */}
                      {call.summary && (
                        <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <div className="w-4 h-4 rounded bg-blue-200 flex items-center justify-center">
                              <MessageSquare className="w-2.5 h-2.5 text-blue-600" />
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-wide text-blue-500">AI Call Summary</div>
                          </div>
                          <p className="text-xs text-blue-900 leading-relaxed">{call.summary}</p>
                        </div>
                      )}

                      {/* Twilio cost */}
                      {showWibizCostBasis && call.cost && (
                        <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
                          <div className="flex items-center gap-1.5 mb-2">
                            <div className="w-4 h-4 rounded bg-amber-200 flex items-center justify-center">
                              <DollarSign className="w-2.5 h-2.5 text-amber-700" />
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700">Twilio Cost</div>
                            <Badge variant="outline" className="ml-auto text-[10px] border-amber-200 text-amber-700 bg-white/70">
                              {costSourceLabel(call.cost.twilioBillingSource)}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700/70">Charge</div>
                              <div className="font-bold text-amber-900">{costLabel(call.cost)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700/70">Source</div>
                              <div className="font-semibold text-amber-900">{costSourceLabel(call.cost.twilioBillingSource)}</div>
                              <div className="text-[10px] text-amber-700/70">{call.cost.twilioPriceUnit ?? "USD"}</div>
                            </div>
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700/70">Call SID</div>
                              <div className="font-mono text-[10px] text-amber-900 break-all">{call.cost.twilioCallSid ?? "—"}</div>
                            </div>
                          </div>
                          {call.cost.notes?.length ? (
                            <p className="text-[10px] text-amber-700/80 mt-2">{call.cost.notes.filter((note: string) => note.includes("Twilio")).join(" ")}</p>
                          ) : null}
                        </div>
                      )}

                      {/* Wibiz Voice usage */}
                      {call.cost?.elDurationMins != null && (
                        <div className="rounded-xl bg-[#84ab4a]/8 border border-[#84ab4a]/20 px-4 py-3">
                          <div className="flex items-center gap-1.5 mb-2">
                            <div className="w-4 h-4 rounded bg-[#84ab4a]/20 flex items-center justify-center">
                              <Mic className="w-2.5 h-2.5 text-[#84ab4a]" />
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-wide text-[#84ab4a]">Wibiz Voice Usage</div>
                            <Badge variant="outline" className="ml-auto text-[10px] border-[#84ab4a]/30 text-[#84ab4a] bg-white/70">
                              {call.cost.elBillingSource === "api" ? "API" : call.cost.elBillingSource === "duration_only" ? "Estimated" : "—"}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-[#84ab4a]/70">Duration</div>
                              <div className="font-bold text-gray-900">{call.cost.elDurationMins}m</div>
                            </div>
                            {call.cost.elCharactersUsed != null && (
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-wide text-[#84ab4a]/70">Characters Used</div>
                                <div className="font-semibold text-gray-900">{call.cost.elCharactersUsed.toLocaleString()}</div>
                              </div>
                            )}
                            {call.cost.elCostUsd != null && (
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-wide text-[#84ab4a]/70">Voice Cost</div>
                                <div className="font-bold text-gray-900">{fmtMoney(call.cost.elCostUsd)}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Full transcript */}
                      {call.transcript && call.transcript.length > 0 ? (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-3 flex items-center gap-1.5">
                            <span className="w-3 h-px bg-gray-200 inline-block" />
                            Full Conversation
                            <span className="flex-1 h-px bg-gray-200 inline-block" />
                          </div>
                          <div className="space-y-3">
                            {call.transcript.map((turn: any, i: number) => {
                              const agentTurn = turn.role === "agent" || turn.role === "assistant";
                              return (
                                <div key={i} className={`flex gap-2.5 ${agentTurn ? "" : "flex-row-reverse"}`}>
                                  {/* Avatar */}
                                  <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${agentTurn
                                    ? (isDawn ? "bg-violet-500 text-white" : "bg-[#84ab4a] text-white")
                                    : "bg-gray-200 text-gray-600"}`}>
                                    {agentTurn ? (call.agentLabel?.[0] ?? "A") : "C"}
                                  </div>
                                  {/* Bubble */}
                                  <div className={`max-w-[76%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed shadow-sm ${agentTurn
                                    ? "bg-white border border-gray-200 text-gray-800 rounded-tl-sm"
                                    : "bg-[#84ab4a]/10 border border-[#84ab4a]/20 text-gray-800 rounded-tr-sm"}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`text-[9px] font-bold uppercase tracking-wide ${agentTurn ? (isDawn ? "text-violet-500" : "text-[#84ab4a]") : "text-gray-400"}`}>
                                        {agentTurn ? (call.agentLabel ?? "Agent") : "Caller"}
                                      </span>
                                      {turn.timeInCallSecs != null && (
                                        <span className="text-[9px] text-gray-400 font-mono">@{turn.timeInCallSecs}s</span>
                                      )}
                                    </div>
                                    <p>{turn.message}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : call.summary ? (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-4 text-center">
                          <p className="text-xs text-gray-400">Full conversation transcript not captured for this call.</p>
                          <p className="text-[10px] text-gray-400 mt-1">
                            Enable transcript capture in voice settings to capture full conversations.
                          </p>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-4 text-center">
                          <p className="text-xs text-gray-400">No transcript or summary captured for this call.</p>
                        </div>
                      )}

                      {/* Quick callback from transcript */}
                      {call.callerNumber && (
                        <div className="pt-1 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-[11px] text-gray-400 font-mono">
                            Caller: {call.callerNumber.slice(-4).padStart(call.callerNumber.length, "*")}
                          </span>
                          <button onClick={() => handleCallback(call.callerNumber)}
                            className="flex items-center gap-1.5 text-xs font-semibold text-violet-500 hover:text-violet-700 transition-colors">
                            <RotateCcw className="w-3.5 h-3.5" />Call Back
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-[11px] text-gray-400 pb-4 px-1">
          <span>Showing {filtered.length} of {allCalls.length} calls · Auto-refreshes every 15 s</span>
          <span>Records reset on server restart</span>
        </div>

        {/* ── Persistent Call History ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Section header */}
          <div className="px-5 pt-5 pb-4 border-b border-gray-100">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#84ab4a]/10 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-[#84ab4a]" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 text-sm">Persistent Call History</h2>
                  <p className="text-[11px] text-gray-400 mt-0.5">Persistent records — transcripts and audio playback. Survives server restarts.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <FilterGroup label="Agent" value={elFilter} onChange={v => setElFilter(v as AgentFilter)}
                  options={[{ v: "all", l: "All" }, { v: "grace", l: "Grace" }, { v: "dawn", l: "Dawn" }]} />
                <button onClick={() => elConvsQuery.refetch()} disabled={elConvsQuery.isFetching}
                  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Refresh">
                  <RefreshCw className={`w-3.5 h-3.5 ${elConvsQuery.isFetching ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Loading */}
          {elConvsQuery.isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-[#84ab4a] animate-spin" />
            </div>
          )}

          {/* Empty */}
          {!elConvsQuery.isLoading && (elConvsQuery.data ?? []).length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <PhoneMissed className="w-6 h-6 text-gray-300" />
              </div>
              <p className="text-sm font-semibold text-gray-400">No conversations found yet.</p>
            </div>
          )}

          {/* Column header */}
          {(elConvsQuery.data ?? []).length > 0 && (
            <div className="hidden lg:grid grid-cols-[60px_64px_80px_1fr_130px_70px_96px_140px] gap-2 px-4 py-2.5
              text-[10px] font-bold uppercase tracking-wide text-gray-400 bg-gray-50/80 border-b border-gray-100">
              <div>Dir</div><div>Agent</div><div>Date</div><div>Time</div>
              <div>Caller / Callee</div><div>Duration</div>
              <div>Status</div><div></div>
            </div>
          )}

          {/* Rows */}
          <div className="divide-y divide-gray-50">
            {(elConvsQuery.data ?? []).map((conv: any) => {
              const isExpanded  = expandedElId === conv.conversationId;
              const won         = conv.callSuccessful === "true" || conv.callSuccessful === "success";
              const failed      = conv.callSuccessful === "false";
              const dur         = fmtDur(conv.durationSecs);
              const ts          = conv.startTimeUnixSecs ? new Date(conv.startTimeUnixSecs * 1000) : null;
              const date        = ts ? ts.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
              const time        = ts ? ts.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "";
              const caller      = conv.callerNumber ? `****${String(conv.callerNumber).slice(-4)}` : null;
              const callee      = conv.calleeNumber ? `****${String(conv.calleeNumber).slice(-4)}` : null;
              const isDawn      = conv.agentLabel === "Dawn";
              const calleeIsAgent = (conv.calleeNumber ?? "").endsWith("9559") || (conv.calleeNumber ?? "").endsWith("7337");
              const callerIsAgent = (conv.callerNumber ?? "").endsWith("9559") || (conv.callerNumber ?? "").endsWith("7337");
              const elDir       = calleeIsAgent ? "inbound" : callerIsAgent ? "outbound" : null;
              const audioUrl    = elAudioUrl[conv.conversationId];
              const audioLoad   = elAudioLoading.has(conv.conversationId);
              const detail      = isExpanded ? elDetailQuery.data : null;
              const detailLoad  = isExpanded && elDetailQuery.isFetching;
              const visibleCost = detail?.cost ?? conv.cost;

              return (
                <div key={conv.conversationId}>
                  <div className={`grid grid-cols-[60px_64px_auto_1fr] lg:grid-cols-[60px_64px_80px_1fr_130px_70px_96px_140px]
                    gap-2 items-center px-4 py-3 hover:bg-gray-50/70 transition-colors`}>

                    {/* Direction */}
                    <div><DirBadge direction={elDir ?? undefined} /></div>

                    {/* Agent */}
                    <div>
                      <Badge variant="outline"
                        className={`text-[10px] font-bold uppercase ${isDawn
                          ? "border-violet-200 text-violet-700 bg-violet-50"
                          : "border-emerald-200 text-emerald-700 bg-emerald-50"}`}>
                        {conv.agentLabel}
                      </Badge>
                    </div>

                    {/* Date */}
                    <div className="hidden lg:block text-xs font-medium text-gray-700">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-gray-300" />{date}</span>
                    </div>

                    {/* Time */}
                    <div className="min-w-0">
                      <div className="text-xs text-gray-500">{time}</div>
                      <div className="lg:hidden text-[10px] text-gray-400 font-mono mt-0.5 truncate">
                        {caller || callee || "—"} · {dur}
                      </div>
                    </div>

                    {/* Caller/callee */}
                    <div className="hidden lg:block text-xs font-mono text-gray-500">
                      {caller && <span>{caller}</span>}
                      {caller && callee && caller !== callee && <span className="text-gray-300 mx-1">→</span>}
                      {callee && caller !== callee && <span className="text-gray-400">{callee}</span>}
                      {!caller && !callee && "—"}
                    </div>

                    {/* Duration */}
                    <div className="hidden lg:flex items-center gap-1 text-xs font-semibold text-gray-700">
                      <Clock className="w-3 h-3 text-gray-300" />{dur}
                    </div>

                    {/* Status */}
                    <div className="hidden lg:block">
                      {won ? (
                        <Badge variant="outline" className="text-[10px] flex items-center gap-1 w-fit border-emerald-200 text-emerald-700 bg-emerald-50">
                          <CheckCircle2 className="w-2.5 h-2.5" />Completed
                        </Badge>
                      ) : failed ? (
                        <Badge variant="outline" className="text-[10px] flex items-center gap-1 w-fit border-rose-200 text-rose-700 bg-rose-50">
                          <XCircle className="w-2.5 h-2.5" />Failed
                        </Badge>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </div>

                    {/* Transcript toggle */}
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => setExpandedElId(isExpanded ? null : conv.conversationId)}
                        className="flex items-center gap-1 text-[10px] font-semibold text-[#84ab4a] hover:text-[#6e9038] transition-colors">
                        <MessageSquare className="w-3 h-3" />
                        <span className="hidden sm:inline">{isExpanded ? "Hide" : "Transcript"}</span>
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>

                  {/* Transcript drawer */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gradient-to-b from-gray-50 to-white px-4 py-4 space-y-4">

                      {detailLoad && (
                        <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading transcript…
                        </div>
                      )}

                      {/* Audio player */}
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5">
                          <span className="w-3 h-px bg-gray-200 inline-block" />Call Recording
                          <span className="flex-1 h-px bg-gray-200 inline-block" />
                        </div>
                        {audioUrl ? (
                          <audio controls src={audioUrl} style={{ height: 32, width: "100%" }} />
                        ) : (
                          <button
                            onClick={() => loadElAudio(conv.conversationId)}
                            disabled={audioLoad}
                            className="flex items-center gap-1.5 text-xs font-semibold text-violet-500 hover:text-violet-700 transition-colors disabled:opacity-50">
                            {audioLoad
                              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading audio…</>
                              : <><PhoneCall className="w-3.5 h-3.5" />Play Recording</>}
                          </button>
                        )}
                      </div>

                      {/* Twilio cost */}
                      {showWibizCostBasis && visibleCost && (
                        <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
                          <div className="flex items-center gap-1.5 mb-2">
                            <div className="w-4 h-4 rounded bg-amber-200 flex items-center justify-center">
                              <DollarSign className="w-2.5 h-2.5 text-amber-700" />
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700">Twilio Cost</div>
                            <Badge variant="outline" className="ml-auto text-[10px] border-amber-200 text-amber-700 bg-white/70">
                              {costSourceLabel(visibleCost.twilioBillingSource)}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700/70">Charge</div>
                              <div className="font-bold text-amber-900">{costLabel(visibleCost)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700/70">Source</div>
                              <div className="font-semibold text-amber-900">{costSourceLabel(visibleCost.twilioBillingSource)}</div>
                              <div className="text-[10px] text-amber-700/70">{visibleCost.twilioPriceUnit ?? "USD"}</div>
                            </div>
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700/70">Call SID</div>
                              <div className="font-mono text-[10px] text-amber-900 break-all">{visibleCost.twilioCallSid ?? "—"}</div>
                            </div>
                          </div>
                          {visibleCost.notes?.length ? (
                            <p className="text-[10px] text-amber-700/80 mt-2">{visibleCost.notes.filter((note: string) => note.includes("Twilio")).join(" ")}</p>
                          ) : null}
                        </div>
                      )}

                      {/* AI Summary */}
                      {detail?.transcriptSummary && (
                        <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <div className="w-4 h-4 rounded bg-blue-200 flex items-center justify-center">
                              <MessageSquare className="w-2.5 h-2.5 text-blue-600" />
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-wide text-blue-500">AI Call Summary</div>
                          </div>
                          <p className="text-xs text-blue-900 leading-relaxed">{detail.transcriptSummary}</p>
                        </div>
                      )}

                      {/* Full transcript */}
                      {detail?.transcript && detail.transcript.length > 0 ? (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-3 flex items-center gap-1.5">
                            <span className="w-3 h-px bg-gray-200 inline-block" />
                            Full Conversation
                            <span className="flex-1 h-px bg-gray-200 inline-block" />
                          </div>
                          <div className="space-y-3">
                            {detail.transcript.map((turn: any, i: number) => {
                              const agentTurn = turn.role === "agent" || turn.role === "assistant";
                              return (
                                <div key={i} className={`flex gap-2.5 ${agentTurn ? "" : "flex-row-reverse"}`}>
                                  <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${agentTurn
                                    ? (isDawn ? "bg-violet-500 text-white" : "bg-[#84ab4a] text-white")
                                    : "bg-gray-200 text-gray-600"}`}>
                                    {agentTurn ? (conv.agentLabel?.[0] ?? "A") : "C"}
                                  </div>
                                  <div className={`max-w-[76%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed shadow-sm ${agentTurn
                                    ? "bg-white border border-gray-200 text-gray-800 rounded-tl-sm"
                                    : "bg-[#84ab4a]/10 border border-[#84ab4a]/20 text-gray-800 rounded-tr-sm"}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`text-[9px] font-bold uppercase tracking-wide ${agentTurn ? (isDawn ? "text-violet-500" : "text-[#84ab4a]") : "text-gray-400"}`}>
                                        {agentTurn ? (conv.agentLabel ?? "Agent") : "Caller"}
                                      </span>
                                      {turn.timeInCallSecs != null && (
                                        <span className="text-[9px] text-gray-400 font-mono">@{turn.timeInCallSecs}s</span>
                                      )}
                                    </div>
                                    <p>{turn.message}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : !detailLoad && (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-4 text-center">
                          <p className="text-xs text-gray-400">Full transcript not captured for this call.</p>
                          <p className="text-[10px] text-gray-400 mt-1">
                            Enable transcript capture in voice settings to capture full conversations.
                          </p>
                        </div>
                      )}

                      {/* Meta */}
                      {conv.conversationId && (
                        <div className="pt-1 border-t border-gray-100">
                          <span className="text-[10px] text-gray-400 font-mono">ID: {conv.conversationId}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* EL footer */}
          {(elConvsQuery.data ?? []).length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 text-[11px] text-gray-400">
              Showing {(elConvsQuery.data ?? []).length} records · Auto-refreshes every 30 s
            </div>
          )}
        </div>

      </main>
    </div>
  );
}

// ── Tiny filter group component ───────────────────────────────────────────────
function FilterGroup({ label, value, onChange, options }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { v: string; l: string }[];
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 hidden sm:block">{label}</span>
      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-[11px] font-semibold">
        {options.map(o => (
          <button key={o.v} onClick={() => onChange(o.v)}
            className={`px-2.5 py-1.5 transition-colors whitespace-nowrap ${value === o.v
              ? "bg-gray-800 text-white"
              : "bg-white text-gray-500 hover:bg-gray-50"}`}>
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Page root ─────────────────────────────────────────────────────────────────
export default function VoiceCommandCenter() {
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "true",
  );

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setUnlocked(false);
  };

  if (!unlocked) return <VoiceCenterLogin onUnlock={() => setUnlocked(true)} />;
  return <CallCenter onLogout={handleLogout} />;
}
